<script setup lang="ts">
import { object, string } from 'yup'

// Not requiring a full user here due to vacancy/contact person use case.
interface UserStartConversationProps {
  id: id
  firstName: string
  lastName: string
  avatarUrl?: string
  // canReceiveMessages?: boolean // not in use anymore?
}

const props = defineProps<{
  user: UserStartConversationProps | UserStartConversationProps[]
  // If a pre-defined subject is provided, the subject input will be hidden
  subject?: string
}>()

const users = computed(() => Array.isArray(props.user) ? props.user : [props.user])

const { isAuthenticated } = useAuth()

const { t } = useI18n({
  useScope: 'local',
})

const showModal = ref<boolean>(false)
const closeModal = () => (showModal.value = false)
const openModal = () => (showModal.value = true)

const schema = object({
  subject: string().required().label(t('conversation.attributes.subject')),
  body: string().required().label(t('conversation.attributes.body')),
})

const isCreating = ref(false)

const initialValues = {
  subject: props.subject ?? '',
  body: '',
}

const { handleSubmit, resetForm, setFieldError } = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues,
})

const { handleServerError } = useServerErrorHandler(schema, setFieldError)
const { handleValidationError } = useFormValidationErrorHandler()

const onSubmit = handleSubmit(async (values) => {
  isCreating.value = true
  try {
    const payload = {
      subject: values.subject,
      userIds: users.value.map(user => user.id),
      body: values.body,
    }
    await createConversation(payload)

    // TODO: maybe show a success message instead?
    navigateTo('/conversations')

    closeModal()
    resetForm()
  }
  catch (error) {
    handleServerError(error)
  }
  finally {
    isCreating.value = false
  }
}, handleValidationError)
</script>

<template>
  <BaseButton @click="openModal">
    <slot>
      <Icon name="ph:chat-duotone" class="-ms-1 size-4" />
      <span>{{ t('sendMessage') }}</span>
    </slot>

    <Teleport to="body">
      <TairoModal
        :open="showModal"
        size="md"
        @close="closeModal"
      >
        <div class="p-6">
          <form
            v-if="isAuthenticated"
            method="POST"
            class="flex flex-col gap-2"
            @submit.prevent="onSubmit"
          >
            <BaseHeading>{{ t('modalTitle') }}</BaseHeading>

            <div
              v-for="u in users"
              :key="u.id"
              class="flex items-center gap-x-2"
            >
              <UserAvatar :user="u" class="" />
              <BaseHeading
                as="h3"
                size="lg"
                weight="medium"
              >
                {{ u.firstName }} {{ u.lastName }}
              </BaseHeading>
            </div>

            <FormField
              v-if="!props.subject"
              name="subject"
              :label="$t('conversation.attributes.subject')"
              :placeholder="$t('conversation.placeholders.subject')"
            />

            <FormTextarea
              name="body"
              :label="$t('conversation.attributes.body')"
              :placeholder="$t('conversation.placeholders.body')"
              rows="5"
            />

            <BaseButton type="submit" color="primary">
              <Icon name="ph:chat-duotone" class="-ms-1 size-4" />
              <span>{{ t('sendMessage') }}</span>
            </BaseButton>
          </form>
          <div v-else class="flex flex-col gap-2">
            <NudgeSignInRequired />
          </div>
        </div>
      </TairoModal>
    </Teleport>
  </BaseButton>
</template>

<i18n lang="json">
{
  "en": {
    "sendMessage": "Send message",
    "modalTitle": "Send a message"
  },
  "nl": {
    "sendMessage": "Stuur bericht",
    "modalTitle": "Stuur een bericht"
  }
}
</i18n>
